import {
  CONNECTED_PROTOCOL_VERSION,

  type GameCommandProcessedMessage,
  type GameSessionCreatedMessage,
  type HostedGameServerMessage,
  type HostedSessionServerMessage,

  type PartyCreatedMessage,
  type PartyJoinedMessage,
  type PartyResumedMessage,
  type PartyStartedMessage,

  type ServerConnectionMessage,
  type ServerWelcomeMessage,
} from "@cue-the-night/transport";

type ServerMessage =
  | ServerConnectionMessage
  | HostedSessionServerMessage
  | HostedGameServerMessage;

type PartySnapshotMessage =
  Extract<
    HostedSessionServerMessage,
    {
      readonly type:
        "PARTY_SNAPSHOT";
    }
  >;

type GameSnapshotMessage =
  Extract<
    HostedGameServerMessage,
    {
      readonly type:
        "GAME_SNAPSHOT";
    }
  >;

type RequestResponse =
  | HostedSessionServerMessage
  | HostedGameServerMessage;

export class HostedPartyClientError
  extends Error {
  constructor(
    public readonly code:
      string,
    message: string,
  ) {
    super(message);

    this.name =
      "HostedPartyClientError";
  }
}

export class HostedPartyClient {
  private socket:
    WebSocket | undefined;

  private heartbeat:
    number | undefined;

  private pushListener:
    ((event: MessageEvent) => void)
    | undefined;

  private readonly partySnapshotHandlers =
    new Set<
      (
        message:
          PartySnapshotMessage,
      ) => void
    >();

  private readonly gameSnapshotHandlers =
    new Set<
      (
        message:
          GameSnapshotMessage,
      ) => void
    >();

  constructor(
    private readonly url:
      string,
    private readonly onUnexpectedDisconnect?:
      () => void,
  ) {}

  subscribeToPartySnapshots(
    handler:
      (
        message:
          PartySnapshotMessage,
      ) => void,
  ): () => void {
    this.partySnapshotHandlers.add(
      handler,
    );

    return () => {
      this.partySnapshotHandlers.delete(
        handler,
      );
    };
  }

  subscribeToGameSnapshots(
    handler:
      (
        message:
          GameSnapshotMessage,
      ) => void,
  ): () => void {
    this.gameSnapshotHandlers.add(
      handler,
    );

    return () => {
      this.gameSnapshotHandlers.delete(
        handler,
      );
    };
  }

  async connect():
  Promise<ServerWelcomeMessage> {
    if (this.socket) {
      throw new Error(
        "Client is already connected.",
      );
    }

    const socket =
      new WebSocket(
        this.url,
      );

    this.socket =
      socket;

    this.pushListener =
      (
        event:
          MessageEvent,
      ) => {
        let message:
          ServerMessage;

        try {
          message =
            JSON.parse(
              String(
                event.data,
              ),
            ) as
              ServerMessage;
        } catch {
          return;
        }

        if (
          message.type ===
          "PARTY_SNAPSHOT"
        ) {
          for (
            const handler of
            this
              .partySnapshotHandlers
          ) {
            handler(
              message,
            );
          }

          return;
        }

        if (
          message.type ===
          "GAME_SNAPSHOT"
        ) {
          for (
            const handler of
            this
              .gameSnapshotHandlers
          ) {
            handler(
              message,
            );
          }
        }
      };

    socket.addEventListener(
      "message",
      this.pushListener,
    );

    socket.addEventListener(
      "close",
      () => {
        if (
          this.socket !==
            socket
        ) {
          return;
        }

        if (
          this.heartbeat !==
            undefined
        ) {
          window.clearInterval(
            this.heartbeat,
          );

          this.heartbeat =
            undefined;
        }

        this.socket =
          undefined;

        this.pushListener =
          undefined;

        this.onUnexpectedDisconnect
          ?.();
      },
      {
        once:
          true,
      },
    );

    await new Promise<void>(
      (
        resolve,
        reject,
      ) => {
        socket.addEventListener(
          "open",
          () =>
            resolve(),
          {
            once: true,
          },
        );

        socket.addEventListener(
          "error",
          () =>
            reject(
              new Error(
                "Unable to connect to CUE THE NIGHT.",
              ),
            ),
          {
            once: true,
          },
        );
      },
    );

    const response =
      this.waitForMessage(
        (message) =>
          message.type ===
            "WELCOME" ||
          message.type ===
            "PROTOCOL_ERROR",
      );

    socket.send(
      JSON.stringify({
        type:
          "HELLO",

        protocolVersion:
          CONNECTED_PROTOCOL_VERSION,

        sentAt:
          new Date()
            .toISOString(),
      }),
    );

    const message =
      await response;

    if (
      message.type ===
      "PROTOCOL_ERROR"
    ) {
      throw new HostedPartyClientError(
        message.code,
        message.message,
      );
    }

    if (
      message.type !==
      "WELCOME"
    ) {
      throw new Error(
        `Expected WELCOME, received ${message.type}.`,
      );
    }

    this.startHeartbeat(
      message
        .heartbeatIntervalMs,
    );

    return message;
  }

  async createParty(
    activeVirtualHostId:
      string,
  ): Promise<PartyCreatedMessage> {
    const response =
      await this.sendRequest({
        type:
          "CREATE_PARTY",

        requestId:
          crypto.randomUUID(),

        activeVirtualHostId,

        sentAt:
          new Date()
            .toISOString(),
      });

    if (
      response.type ===
      "SESSION_ERROR"
    ) {
      throw new HostedPartyClientError(
        response.code,
        response.message,
      );
    }

    if (
      response.type !==
      "PARTY_CREATED"
    ) {
      throw new Error(
        `Unexpected response: ${response.type}`,
      );
    }

    return response;
  }

  async joinParty(
    joinCode:
      string,
    displayName:
      string,
    avatarId:
      string,
  ): Promise<PartyJoinedMessage> {
    const response =
      await this.sendRequest({
        type:
          "JOIN_PARTY",

        requestId:
          crypto.randomUUID(),

        joinCode,
        displayName,
        avatarId,

        sentAt:
          new Date()
            .toISOString(),
      });

    if (
      response.type ===
      "SESSION_ERROR"
    ) {
      throw new HostedPartyClientError(
        response.code,
        response.message,
      );
    }

    if (
      response.type !==
      "PARTY_JOINED"
    ) {
      throw new Error(
        `Unexpected response: ${response.type}`,
      );
    }

    return response;
  }

  async resumeParty(
    reconnectToken:
      string,
  ): Promise<PartyResumedMessage> {
    const response =
      await this.sendRequest({
        type:
          "RESUME_PARTY",

        requestId:
          crypto.randomUUID(),

        reconnectToken,

        sentAt:
          new Date()
            .toISOString(),
      });

    if (
      response.type ===
      "SESSION_ERROR"
    ) {
      throw new HostedPartyClientError(
        response.code,
        response.message,
      );
    }

    if (
      response.type !==
      "PARTY_RESUMED"
    ) {
      throw new Error(
        `Unexpected response: ${response.type}`,
      );
    }

    return response;
  }

  async startParty():
  Promise<PartyStartedMessage> {
    const response =
      await this.sendRequest({
        type:
          "START_PARTY",

        requestId:
          crypto.randomUUID(),

        sentAt:
          new Date()
            .toISOString(),
      });

    if (
      response.type ===
      "SESSION_ERROR"
    ) {
      throw new HostedPartyClientError(
        response.code,
        response.message,
      );
    }

    if (
      response.type !==
      "PARTY_STARTED"
    ) {
      throw new Error(
        `Unexpected response: ${response.type}`,
      );
    }

    return response;
  }

  async createGameSession(
    gameId:
      string,
    settings:
      Readonly<
        Record<
          string,
          unknown
        >
      >,
  ): Promise<GameSessionCreatedMessage> {
    const response =
      await this.sendRequest({
        type:
          "CREATE_GAME_SESSION",

        requestId:
          crypto.randomUUID(),

        gameId,
        settings,

        sentAt:
          new Date()
            .toISOString(),
      });

    if (
      response.type ===
      "GAME_ERROR"
    ) {
      throw new HostedPartyClientError(
        response.code,
        response.message,
      );
    }

    if (
      response.type !==
      "GAME_SESSION_CREATED"
    ) {
      throw new Error(
        `Unexpected response: ${response.type}`,
      );
    }

    return response;
  }

  async endGameSession(
    gameSessionId:
      string,
  ) {
    const response =
      await this.sendRequest({
        type:
          "END_GAME_SESSION",

        requestId:
          crypto.randomUUID(),

        gameSessionId,

        sentAt:
          new Date()
            .toISOString(),
      });

    if (
      response.type ===
      "GAME_ERROR"
    ) {
      throw new HostedPartyClientError(
        response.code,
        response.message,
      );
    }

    if (
      response.type !==
      "GAME_SESSION_ENDED"
    ) {
      throw new Error(
        `Unexpected response: ${response.type}`,
      );
    }

    return response;
  }

  async sendGameCommand(
    gameSessionId:
      string,
    expectedStateVersion:
      number,
    command:
      Readonly<
        Record<
          string,
          unknown
        >
      >,
  ): Promise<GameCommandProcessedMessage> {
    const response =
      await this.sendRequest({
        type:
          "GAME_COMMAND",

        requestId:
          crypto.randomUUID(),

        gameSessionId,

        commandId:
          crypto.randomUUID(),

        expectedStateVersion,

        command,

        sentAt:
          new Date()
            .toISOString(),
      });

    if (
      response.type ===
      "GAME_ERROR"
    ) {
      throw new HostedPartyClientError(
        response.code,
        response.message,
      );
    }

    if (
      response.type !==
      "GAME_COMMAND_PROCESSED"
    ) {
      throw new Error(
        `Unexpected response: ${response.type}`,
      );
    }

    return response;
  }

  disconnect(): void {
    if (
      this.heartbeat !==
      undefined
    ) {
      window.clearInterval(
        this.heartbeat,
      );

      this.heartbeat =
        undefined;
    }

    if (
      this.socket &&
      this.pushListener
    ) {
      this.socket
        .removeEventListener(
          "message",
          this.pushListener,
        );
    }

    this.pushListener =
      undefined;

    this.socket?.close(
      1000,
      "player-web closing",
    );

    this.socket =
      undefined;
  }

  private async sendRequest(
    message:
      Record<
        string,
        unknown
      > & {
        readonly requestId:
          string;
      },
  ): Promise<RequestResponse> {
    const socket =
      this.requireSocket();

    const response =
      this.waitForMessage(
        (incoming) =>
          (
            "requestId" in
              incoming &&
            incoming.requestId ===
              message.requestId
          ) ||
          incoming.type ===
            "PROTOCOL_ERROR",
      );

    socket.send(
      JSON.stringify(
        message,
      ),
    );

    const result =
      await response;

    if (
      result.type ===
      "PROTOCOL_ERROR"
    ) {
      throw new HostedPartyClientError(
        result.code,
        result.message,
      );
    }

    if (
      !(
        "requestId" in
        result
      )
    ) {
      throw new Error(
        "Expected request response.",
      );
    }

    return result;
  }

  private waitForMessage(
    predicate:
      (
        message:
          ServerMessage,
      ) => boolean,
  ): Promise<ServerMessage> {
    const socket =
      this.requireSocket();

    return new Promise(
      (
        resolve,
        reject,
      ) => {
        const timeout =
          window.setTimeout(
            () => {
              cleanup();

              reject(
                new Error(
                  "CUE THE NIGHT server timed out.",
                ),
              );
            },
            15_000,
          );

        const onMessage =
          (
            event:
              MessageEvent,
          ) => {
            let message:
              ServerMessage;

            try {
              message =
                JSON.parse(
                  String(
                    event.data,
                  ),
                ) as
                  ServerMessage;
            } catch {
              return;
            }

            if (
              !predicate(
                message,
              )
            ) {
              return;
            }

            cleanup();

            resolve(
              message,
            );
          };

        const onClose =
          () => {
            cleanup();

            reject(
              new Error(
                "Connection to CUE THE NIGHT was closed.",
              ),
            );
          };

        const cleanup =
          () => {
            window.clearTimeout(
              timeout,
            );

            socket.removeEventListener(
              "message",
              onMessage,
            );

            socket.removeEventListener(
              "close",
              onClose,
            );
          };

        socket.addEventListener(
          "message",
          onMessage,
        );

        socket.addEventListener(
          "close",
          onClose,
        );
      },
    );
  }

  private startHeartbeat(
    intervalMs:
      number,
  ): void {
    const socket =
      this.requireSocket();

    this.heartbeat =
      window.setInterval(
        () => {
          if (
            socket.readyState !==
            WebSocket.OPEN
          ) {
            return;
          }

          socket.send(
            JSON.stringify({
              type:
                "HEARTBEAT",

              protocolVersion:
                CONNECTED_PROTOCOL_VERSION,

              nonce:
                crypto.randomUUID(),

              sentAt:
                new Date()
                  .toISOString(),
            }),
          );
        },
        intervalMs,
      );
  }

  private requireSocket():
  WebSocket {
    if (
      !this.socket ||
      this.socket.readyState !==
        WebSocket.OPEN
    ) {
      throw new Error(
        "CUE THE NIGHT is not connected.",
      );
    }

    return this.socket;
  }
}
